/**
 * zalo-proxy-dialog.tsx — Gắn proxy riêng cho một tài khoản Zalo cá nhân.
 *
 * Nhiều tài khoản Zalo cùng đăng nhập từ MỘT IP máy chủ là dấu hiệu rõ nhất để
 * Zalo nhận ra "tool" và khoá hàng loạt. Mỗi tài khoản đi qua một IP riêng thì
 * trông như từng người dùng thật.
 *
 * Luồng cố ý: Kiểm tra → thấy IP đi ra → mới Lưu. Máy chủ cũng tự kiểm tra lại
 * khi lưu và từ chối proxy chết, vì lưu proxy hỏng = tài khoản rớt mạng ngay khi
 * phiên được dựng lại.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Loader2, ShieldCheck, Shield, Trash2, Wifi } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import dayjs from 'dayjs'
import { useZaloProxy, useTestZaloProxy, useSaveZaloProxy, useRemoveZaloProxy, useZaloProxyHistory } from '@/hooks/use-integrations'
import { useMyPermissions } from '@/hooks/use-settings'

/** Nút nhỏ trên hàng hạn mức: cho biết tài khoản đang đi qua proxy hay IP máy chủ. */
export function ZaloProxyButton({ accountId }: { accountId: string }) {
  const { t } = useTranslation('chanZalo')
  const [mo, setMo] = useState(false)
  const { data } = useZaloProxy(accountId)
  const coProxy = !!data?.configured && !!data.proxy

  return (
    <>
      <button
        type="button"
        onClick={() => setMo(true)}
        className={
          data?.error
            ? 'inline-flex items-center gap-1 font-medium text-destructive hover:underline'
            : coProxy
              ? 'inline-flex items-center gap-1 text-success hover:underline'
              : 'inline-flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline'
        }
        title={data?.error || (coProxy ? t('proxy.btn.viaProxy') : t('proxy.btn.viaServer'))}
      >
        {coProxy ? <ShieldCheck className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
        {data?.error ? t('proxy.btn.error') : coProxy ? t('proxy.btn.label', { proxy: data.proxy?.replace(/^https?:\/\/(\*\*\*@)?/, '') }) : t('proxy.btn.none')}
      </button>
      {mo && <ZaloProxyDialog accountId={accountId} onClose={() => setMo(false)} />}
    </>
  )
}

/**
 * Số liệu đợt thử proxy: IP có giữ được không, proxy có chết không, phiên Zalo
 * có rớt không. Máy chủ tự kiểm tra mỗi 30 phút — người dùng không phải nhớ
 * bấm "Kiểm tra", và quyết định có bán proxy trong hệ thống dựa trên số thật.
 */
function ProxyHistory({ accountId }: { accountId: string }) {
  const { t } = useTranslation('chanZalo')
  const { data: h, isLoading } = useZaloProxyHistory(accountId)
  if (isLoading) return <p className="text-xs text-muted-foreground">{t('proxy.history.loading')}</p>
  if (!h || !h.since)
    return (
      <p className="text-xs text-muted-foreground">
        {t('proxy.history.empty')}
      </p>
    )

  const soNgay = Math.max(1, dayjs().diff(dayjs(h.since), 'day'))
  const o = [
    { id: 'currentIp', nhan: t('proxy.history.currentIp'), giaTri: h.currentIp ?? '—', mono: true },
    { id: 'ipChanges', nhan: t('proxy.history.ipChanges'), giaTri: `${h.ipChanges}${h.lastChangeAt ? t('proxy.history.lastChange', { time: dayjs(h.lastChangeAt).format('HH:mm DD/MM') }) : ''}`, xau: h.ipChanges > 0 },
    { id: 'uptime', nhan: t('proxy.history.uptime'), giaTri: h.uptimePct === null ? '—' : t('proxy.history.uptimeValue', { pct: h.uptimePct }), xau: (h.uptimePct ?? 100) < 95 },
    { id: 'failures', nhan: t('proxy.history.failures'), giaTri: t('proxy.history.failuresValue', { failures: h.failures, checks: h.checks }), xau: h.failures > 0 },
    { id: 'connects', nhan: t('proxy.history.connects'), giaTri: String(h.connects), xau: h.connects > soNgay * 2 },
    {
      id: 'closes',
      nhan: t('proxy.history.closes'),
      giaTri: h.closes.total
        ? t('proxy.history.closesValue', { total: h.closes.total, elsewhere: h.closes.elsewhere, network: h.closes.network }) +
          (h.closes.other ? t('proxy.history.closesOther', { other: h.closes.other }) : '')
        : '0',
      xau: h.closes.total > 0,
    },
  ]

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        {t('proxy.history.since', { time: dayjs(h.since).format('HH:mm DD/MM') })}{' '}
        <span className="font-normal text-muted-foreground">· {t('proxy.history.autoChecks', { checks: h.checks })}</span>
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {o.map((x) => (
          <div key={x.id} className="contents">
            <dt className="text-muted-foreground">{x.nhan}</dt>
            <dd className={`${x.mono ? 'font-mono' : ''} ${x.xau ? 'font-medium text-warning' : ''}`}>{x.giaTri}</dd>
          </div>
        ))}
      </dl>
      {h.closes.elsewhere > 0 && (
        // Chẩn đoán ngay tại chỗ: phần lớn ca "rớt liên tục" là do cùng tài khoản
        // đang mở ở chỗ khác — đổ cho proxy thì đi sửa nhầm chỗ.
        <p className="rounded bg-warning/10 p-2 text-xs">
          <Trans t={t} i18nKey="proxy.history.elsewhere" values={{ n: h.closes.elsewhere }} components={{ b: <strong /> }} />
        </p>
      )}
      {h.changes.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">{t('proxy.history.ipChangeList')}</summary>
          <ul className="mt-1 space-y-0.5 font-mono">
            {h.changes.map((c) => (
              <li key={c.at}>
                {dayjs(c.at).format('HH:mm DD/MM')} · {c.from} → {c.to}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

function ZaloProxyDialog({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const { t } = useTranslation('chanZalo')
  const { data } = useZaloProxy(accountId)
  const [proxy, setProxy] = useState('')
  const [ketQua, setKetQua] = useState<{ ip: string; latencyMs: number } | null>(null)
  const test = useTestZaloProxy()
  const save = useSaveZaloProxy()
  const remove = useRemoveZaloProxy()
  const dangBan = test.isPending || save.isPending || remove.isPending
  // Kiểm tra / lưu / gỡ proxy đều đòi `integrations.update` → thiếu quyền thì hộp chỉ còn trạng thái + lịch sử.
  const canUpdate = !!useMyPermissions().data?.has('integrations.update')

  const kiemTra = () => {
    setKetQua(null)
    test.mutate(
      { accountId, proxy: proxy.trim() || undefined },
      {
        onSuccess: (r) => setKetQua({ ip: r.ip, latencyMs: r.latencyMs }),
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  const luu = () =>
    save.mutate(
      { accountId, proxy },
      {
        onSuccess: (r) => {
          toast.success(
            r.reconnecting
              ? t('proxy.dialog.savedReconnecting', { ip: r.ip })
              : t('proxy.dialog.savedLater', { ip: r.ip }),
          )
          onClose()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )

  const go = () =>
    remove.mutate(accountId, {
      onSuccess: (r) => {
        toast.success(r.reconnecting ? t('proxy.dialog.removedReconnecting') : t('proxy.dialog.removed'))
        onClose()
      },
      onError: (e) => toast.error(apiError(e)),
    })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('proxy.dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('proxy.dialog.desc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            {data?.error ? (
              <span className="text-destructive">{data.error}</span>
            ) : data?.configured && data.proxy ? (
              <>{t('proxy.dialog.inUse')} <span className="font-mono">{data.proxy}</span></>
            ) : (
              <span className="text-muted-foreground">{t('proxy.dialog.notSet')}</span>
            )}
          </div>

          {canUpdate && (
            <div className="space-y-1.5">
              <Label htmlFor="zalo-proxy">{t('proxy.dialog.newProxy')}</Label>
              <Input
                id="zalo-proxy"
                value={proxy}
                onChange={(e) => {
                  setProxy(e.target.value)
                  setKetQua(null)
                }}
                placeholder={t('proxy.dialog.placeholder')}
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                <Trans t={t} i18nKey="proxy.dialog.hint" components={{ b: <strong /> }} />
              </p>
            </div>
          )}

          {data?.configured && (
            <div className="rounded-md border p-3">
              <ProxyHistory accountId={accountId} />
            </div>
          )}

          {ketQua && (
            <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm">
              <Wifi className="h-4 w-4 text-success" />
              <Trans t={t} i18nKey="proxy.dialog.works" values={{ ip: ketQua.ip, ms: ketQua.latencyMs }} components={{ ip: <span className="font-mono font-medium" /> }} />
            </div>
          )}
        </div>

        {canUpdate && (
          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            {data?.configured ? (
              <Button variant="ghost" className="text-destructive" onClick={go} disabled={dangBan}>
                {remove.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
                {t('proxy.dialog.remove')}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={kiemTra} disabled={dangBan || (!proxy.trim() && !data?.configured)}>
                {test.isPending && <Loader2 className="animate-spin" />}
                {t('common:actions.test')}
              </Button>
              <Button onClick={luu} disabled={dangBan || !proxy.trim()}>
                {save.isPending && <Loader2 className="animate-spin" />}
                {t('proxy.dialog.saveReconnect')}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
